import {classNames, Fragment, observer, React} from '@api'
import {Icon, Menu, MenuItem, Slider} from '@blueprintjs/core'
import {IMaterialTooltipPlacement, Popover2, Tooltip2} from '@api'
import {CardSizeSlider, CardSizeSliderCatalog, CardSpacingSliderCatalog, CatalogColumnSliderBlueprint, CatalogColumnSliderMaterial, ToggleAggregationLocationButton, GroupSwatchesManagementPanel, HeroCardsToolbarButton} from '@modules'
import {ui, useCatalog} from '@stores'
import {icons} from '@stores/ui'
import {PrimaryIconButton} from 'centric-ui-components'
import * as css from './GroupByToolbarItems.css'

export const GroupByToolbarItems_MenuFooter = observer(function GroupByToolbarButtons_MenuFooter() {
  const catalog = useCatalog(),
    {strings, isProductView, aggregation: {onToggleShowAggDetailsWhenCollapsed}, settings: {showAggs, groupBy: {showEmptyGroups}, viewMode, aggregateLocation}, toggleShowEmptyGroups} = catalog

  return catalog.hasLoadedConfig &&
         !isProductView && viewMode !== 'table' &&
         <div className={css.toolbarButtons}>
	         <ShowExpandedGroupsFirstToggleButton/>
	         <ShowOnlyExpandedGroupsToggleButton/>

	         <Tooltip2 content={strings.showEmptyGroups} placement='bottom'>
		         <PrimaryIconButton disabled={!catalog.renderEmptyGroups} icon={icons.showEmptyGroups} active={showEmptyGroups} onClick={toggleShowEmptyGroups}/>
	         </Tooltip2>

           {(showAggs || !catalog.catalogConfig?.features?.groupBy?.allowInlineDetailAggregations) &&
	        <Fragment>
              {aggregateLocation === 'inline' &&
	           <Tooltip2 content={strings.showGroupAggregationsWhenCollapsed} placement='bottom'>
		           <PrimaryIconButton icon={<Icon icon='collapse-all'/>} active={catalog.settings.showAggDetailsWhenCollapsed} onClick={onToggleShowAggDetailsWhenCollapsed}/>
	           </Tooltip2>}
	        </Fragment>}
         </div>
})

export const GroupByToolbarItems = observer(function GroupByToolbarButtons() {
  const catalog = useCatalog(),
    {
      strings, isProductView, groupBy: {hierarchyGroups, settings: {}}, aggregation: {onToggleShowAggs, onToggleShowAggDetailsWhenCollapsed}, settings: {toolbarItems, showAggs, aggregateLocation},
      stores: {filterChipsToolbar}, isShowingHeroCards, assortments: {selectedAssortment}
    } = catalog

  return catalog.hasLoadedConfig &&
         <div className={css.toolbarButtons}>
           {((toolbarItems.sliders.location === 'secondary') && !filterChipsToolbar.isShowing) || toolbarItems.sliders.location === 'top' ?
            <Fragment>
              <CatalogColumnSliderMaterial/>
              <CardSpacingSliderCatalog/>
              <CardSizeSliderCatalog/>
            </Fragment> : null}

           {ui.settings.showGroupButtonsInTopBar && !isProductView &&
	        <Fragment>
		        <ColumnViewCatalogModeToggleButton/>

              {hierarchyGroups.some(h => h?.allowCardMode) &&
	           <Fragment>
		           <ToggleFlatModeToolbarButton/>

                 {isShowingHeroCards && <HideCollapsedHeroCardsToggleToolbarButton/>}

               {ui.settings.groupByHeroCards && <HeroCardsToolbarButton/>}
		           <ToggleCardSwatchesButton/>
	           </Fragment>}


              {false &&  // Now handled in catalog view actions part of toolbar
               catalog.catalogConfig?.features?.groupBy?.allowInlineDetailAggregations &&
	           <Tooltip2 content={strings.showGroupAggregations} placement='bottom'>
		           <PrimaryIconButton icon={<Icon icon='grouped-bar-chart'/>} active={showAggs} onClick={onToggleShowAggs}/>
	           </Tooltip2>}

              {showAggs && aggregateLocation === 'inline' &&
	           <Fragment>
		           <ToggleAggregationLocationButton className={classNames(css.showOnHover, css.groupSidebarAggregate)}/>

		           <Tooltip2 content={strings.showGroupAggregationsWhenCollapsed} placement='bottom'>
			           <PrimaryIconButton icon={<Icon icon='collapse-all'/>} active={catalog.settings.showAggDetailsWhenCollapsed} onClick={onToggleShowAggDetailsWhenCollapsed}/>
		           </Tooltip2>
	           </Fragment>}
	        </Fragment>}
         </div>

})

/// region Buttons

export const ToggleFlatModeToolbarButton = observer<{ tooltipPlacement?: IMaterialTooltipPlacement }>(function ToggleFlatModeToolbarButton({tooltipPlacement}) {
  const {strings, groupBy: {hierarchyGroups, settings, settings: {groupView}}} = useCatalog()

  if (hierarchyGroups.some(h => h?.allowCardMode)) {
    return (<Tooltip2 content={strings.flatMode} placement={tooltipPlacement || 'bottom'}>
      <PrimaryIconButton onClick={() => settings.groupView = groupView == 'flat' ? 'card' : 'flat'} active={groupView == 'flat'}><Icon icon='th-list'/></PrimaryIconButton>
    </Tooltip2>)
  }

  return null
})

const HideCollapsedHeroCardsToggleToolbarButton = observer(() => {
  const {groupBy: {settings: {heroCards: {hideCollapsed}}, settings, toggleHideHeroCardsInCollapsedGroups}} = useCatalog()

  return (
    <Tooltip2 content={`Hero Cards`}
              placement='bottom'>
      <PrimaryIconButton active={!hideCollapsed} onClick={toggleHideHeroCardsInCollapsedGroups} icon={<Icon icon='control'/>}/>
    </Tooltip2>
  )
})

export const ShowExpandedGroupsFirstToggleButton = observer(function ShowExpandedGroupsFirstToggleButton() {
  const {groupBy: {toggleShowExpandedFirst, settings: {showExpandedFirst}}} = useCatalog()
  return (
    <Tooltip2 content={'Show Expanded Groups First'} placement='bottom'>
      <div>
        <PrimaryIconButton
          onClick={toggleShowExpandedFirst}
          icon={icons.showExpandedFirst}
          active={showExpandedFirst}/>
      </div>
    </Tooltip2>
  )
})

export const ShowOnlyExpandedGroupsToggleButton = observer(function ShowOnlyExpandedGroupsToggleButton() {
  const {
    strings, groupBy: {settings, viewStore}
  } = useCatalog()

  return viewStore?.mgr?.showOnlyExpandedMenu ? <Tooltip2 content={strings.showOnlyExpandedGroups} placement='bottom'>
    <PrimaryIconButton
      onClick={() => settings.showOnlyExpanded = !settings.showOnlyExpanded}
      active={settings.showOnlyExpanded}
      icon={icons.showOnlyExpanded}>
    </PrimaryIconButton>
  </Tooltip2> : null
})

export const ToggleCardSwatchesButton = observer<{ tooltipPlacement?: IMaterialTooltipPlacement }>(function ToggleCardSwatchesButton({tooltipPlacement}) {
  const {strings, groupBy: {canShowSwatchesAndHeroCards, settings, toggleCardSwatches}, assortments} = useCatalog()
  if (!canShowSwatchesAndHeroCards) return null

  return (
    <Popover2 interactionKind='hover'
              minimal
              hoverOpenDelay={100}
              placement={tooltipPlacement || 'bottom'}
              content={<Menu>
                <GroupSwatchesManagementPanel/>
              </Menu>}>
      <Tooltip2 content={strings.showSwatches} placement={tooltipPlacement || 'bottom'}>
        <PrimaryIconButton
          icon={icons.toggleCardSwatches}
          active={settings.showSwatches === true}
          onClick={toggleCardSwatches} // Switched to popover
        />
      </Tooltip2>
    </Popover2>
  )
})

export const ColumnViewCatalogModeToggleButton = observer<{ tooltipPlacement?: IMaterialTooltipPlacement }>(function ToggleColumnViewCatalogModeButton({tooltipPlacement}) {
  const {strings, groupBy: {settings, settings: {columnView}}, assortments} = useCatalog()

  if (assortments?.hideCatalogSpecificWidgets) {
    return null
  }

  return (
    <Tooltip2 content={strings.groupedColumnView} placement={tooltipPlacement || 'bottom'}>
      <PrimaryIconButton onClick={() => settings.columnView = !columnView} active={columnView}
                         icon={icons.columnView}
      />
    </Tooltip2>)
})

/// endregion


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Menus/Toolbar/GroupByToolbarItems.tsx